import React from 'react';
import { Folder, FileText, Edit, Eye, Download, Trash2 } from 'lucide-react';
import api from '../../api';

const DocumentList = ({
  documents,
  loading,
  onEdit,
  onDelete,
  getTypeInfo
}) => {
  // Fonction utilitaire pour construire l'URL complète
  const buildFullUrl = (relativeUrl) => {
    if (!relativeUrl) return '';
    const baseUrl = 'http://192.168.1.65:8000'; // Même URL que dans api.js
    return relativeUrl.startsWith('http') ? relativeUrl : `${baseUrl}${relativeUrl}`;
  };

  // Nouvelle version : forcer le téléchargement même pour PDF
  const handleDownload = async (doc) => {
    try {
      const fullUrl = buildFullUrl(doc.fichier_url);
      // Ajout du token si besoin
      const headers = {};
      const tokensString = localStorage.getItem('auth_tokens');
      if (tokensString) {
        try {
          const tokens = JSON.parse(tokensString);
          if (tokens.access) {
            headers['Authorization'] = `Bearer ${tokens.access}`;
          }
        } catch {}
      }
      const response = await fetch(fullUrl, { method: 'GET', headers });
      if (!response.ok) throw new Error('Erreur réseau');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nom_fichier || doc.titre || 'document');
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      alert('Erreur lors du téléchargement du document.');
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-gray-200 h-64 rounded-lg animate-pulse w-full max-w-full"></div>
        ))}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="text-center py-12">
        <Folder className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun document trouvé</h3>
        <p className="mt-1 text-sm text-gray-500">
          Commencez par uploader un nouveau document.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      {documents.map((document) => {
        const typeInfo = getTypeInfo(document.type_document);
        return (
          <div key={document.id} className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-200 w-full max-w-full flex flex-col">
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-indigo-100 p-2 rounded-lg">
                    <FileText className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                      {document.titre}
                    </h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${typeInfo.color}`}>
                      {typeInfo.icon} {typeInfo.label}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(document)}
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    title="Modifier"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <a
                    href={buildFullUrl(document.fichier_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    title="Voir le document"
                  >
                    <Eye className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => handleDownload(document)}
                    className="text-gray-400 hover:text-green-600 transition-colors"
                    title="Télécharger"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(document)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-3 flex-1">
                {document.soutenance && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Soutenance:</span>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {document.soutenance.etudiant_principal_info ? 
                        `${document.soutenance.etudiant_principal_info.nom} ${document.soutenance.etudiant_principal_info.prenom}` : 
                        document.soutenance.titre_travail
                      }
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Taille: {document.taille_fichier_mb ? `${document.taille_fichier_mb} MB` : 'Inconnue'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Date(document.date_upload).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                {document.uploaded_by && (
                  <div className="pt-3 border-t border-gray-200">
                    <span className="text-xs text-gray-500">
                      Uploadé par: {document.uploaded_by.first_name} {document.uploaded_by.last_name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DocumentList; 